/* Keeping the app itself up to date (owner request 2026-10-13), the pure part. The app asks /version.json whether a newer
   version is out; if so it either reloads by itself, or shows a small "A new version is ready — Update" bar.
   Rule: an update never loses anything. Tested in tests/app/update.test.ts. */

export interface AppVersion { id: string; commit: string; built: string; label: string }

/** a /version.json answer, or null for anything else (an older site answers with its page instead) */
export function parseVersion(v: unknown): AppVersion | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  return typeof o.id === 'string' && o.id && typeof o.commit === 'string' && typeof o.label === 'string'
    ? {id: o.id, commit: o.commit, built: typeof o.built === 'string' ? o.built : '', label: o.label} : null;
}

/** what is going on in the app right now */
export interface Moment {
  ready: boolean;          // signed in and loaded (the login screen and loading: see below)
  windowOpen: boolean;     // an Add / Edit / any window, or a pick-list, or a suggestion list is open
  fullScreen: boolean;     // the full-screen timer is open (it would close)
  typing: boolean;         // a field is focused (being typed in or picked from)
  pending: number;         // changes not yet sent to the server (the outbox)
  gesture: boolean;        // a finger is on the screen, a row is being swiped, or a pull-to-refresh is under way
  undoOffered: boolean;    // a "Shake to undo" offer is on screen
  loginTyped: boolean;     // the login screen has something typed in it
}
/** may the app reload by itself now? A running timer does not matter: it is kept in the database and on the device. */
export function safeToReload(m: Moment): boolean {
  if (m.pending > 0 || m.typing || m.windowOpen || m.fullScreen || m.gesture || m.undoOffered) return false;
  if (!m.ready) return !m.loginTyped;
  return true;
}
/** the person tapped Update (or Refresh / pulled down): stricter rules do not apply, but nothing may be lost.
    'window': a window is open (what is typed there would be lost) — close it first. 'pending': changes still wait to
    be sent — reload only once they are saved. */
export function tapBlock(m: Pick<Moment, 'windowOpen' | 'pending'>): '' | 'window' | 'pending' {
  return m.windowOpen ? 'window' : m.pending > 0 ? 'pending' : '';
}

/* ---------- no reload loops ---------- */
/** what is remembered (per tab, sessionStorage) about reloads the app did by itself */
export interface Tries { target: string; count: number; at: number }
export const RETRY_AFTER = 10 * 60 * 1000;   // a reload that did not bring the new version: try again at the earliest after this
export const MAX_TRIES = 3;                   // then only on a tap (the bar stays)
/** may the app reload by itself to reach `target`? Not again and again: after a reload that still showed the old version
    (a slow network served the saved page, or the new version is not everywhere yet) it waits for a later check. */
export function mayAutoReload(t: Tries | null, target: string, now: number): boolean {
  if (!t || t.target !== target) return true;
  return t.count < MAX_TRIES && now - t.at >= RETRY_AFTER;
}
export function noteTry(t: Tries | null, target: string, now: number): Tries {
  return {target, count: t && t.target === target ? t.count + 1 : 1, at: now};
}
